import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  InputAdornment,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Collapse,
} from '@mui/material';
import api from '../../services/api';

// Leftover frequency options (percentage-based) - matches onboarding
const leftoverFrequencyOptions = [
  { value: 0, label: 'Never', description: 'No extra portions' },
  { value: 33, label: 'Sometimes', description: 'About 1-2 dinners per week' },
  { value: 50, label: 'Half the time', description: 'Every other dinner' },
  { value: 75, label: 'Most of the time', description: 'Most dinners' },
  { value: 100, label: 'Always', description: 'Every dinner' },
];

const UserPreferences: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Family preferences state
  // Budget field removed from UI — feature isn't implemented yet so we don't
  // collect new values. Existing values in users.budget are preserved.
  const [maxPrepTime, setMaxPrepTime] = useState<string>('45');
  const [likesLeftovers, setLikesLeftovers] = useState(true);
  const [leftoverFrequency, setLeftoverFrequency] = useState<number>(50); // Default 50% (half the time)
  const [mealPlanStartDay, setMealPlanStartDay] = useState<number>(1);
  const [prefLoading, setPrefLoading] = useState(false);

  const daysOfWeek = [
    { value: 0, label: 'Sunday' },
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' },
  ];

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    setLoading(true);
    try {
      const profileRes = await api.get('/users/profile');

      const profile = profileRes.data.user || profileRes.data;
      setMaxPrepTime(profile.max_prep_time?.toString() || '45');
      setLikesLeftovers(profile.likes_leftovers ?? true);

      // Handle legacy values (1-7) by converting to percentage
      const storedFreq = profile.leftover_frequency ?? 50;
      let convertedFreq = storedFreq;
      if (storedFreq > 0 && storedFreq <= 7) {
        // Legacy value - convert roughly to percentage
        // 1-2 → 33%, 3-4 → 50%, 5-6 → 75%, 7 → 100%
        if (storedFreq <= 2) convertedFreq = 33;
        else if (storedFreq <= 4) convertedFreq = 50;
        else if (storedFreq <= 6) convertedFreq = 75;
        else convertedFreq = 100;
      }
      setLeftoverFrequency(convertedFreq);
      setMealPlanStartDay(profile.meal_plan_start_day ?? 1);
    } catch (err: any) {
      setError('Failed to load preferences');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSavePreferences = async () => {
    setError('');
    setSuccess('');

    const prepTimeNum = parseInt(maxPrepTime);
    if (!maxPrepTime || isNaN(prepTimeNum) || prepTimeNum < 5 || prepTimeNum > 180) {
      setError('Please enter a prep time between 5 and 180 minutes');
      return;
    }

    setPrefLoading(true);
    try {
      await api.patch('/users/profile', {
        max_prep_time: prepTimeNum,
        likes_leftovers: likesLeftovers,
        leftover_frequency: likesLeftovers ? leftoverFrequency : 0,
        meal_plan_start_day: mealPlanStartDay,
      });
      setSuccess('Preferences updated successfully!');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update preferences');
    } finally {
      setPrefLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading preferences...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Family Preferences
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        These settings apply to your entire family's meal plans
      </Typography>

      <Paper sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <TextField
          fullWidth
          label="Maximum Prep Time"
          type="number"
          value={maxPrepTime}
          onChange={(e) => setMaxPrepTime(e.target.value)}
          required
          InputProps={{
            endAdornment: <InputAdornment position="end">minutes</InputAdornment>,
          }}
          helperText="How much time can you spend preparing meals? (5-180 minutes)"
          sx={{ mb: 3 }}
        />

        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel id="meal-plan-start-day-label">Meal Plan Start Day</InputLabel>
          <Select
            labelId="meal-plan-start-day-label"
            id="meal-plan-start-day"
            value={mealPlanStartDay}
            label="Meal Plan Start Day"
            onChange={(e) => setMealPlanStartDay(Number(e.target.value))}
          >
            {daysOfWeek.map((day) => (
              <MenuItem key={day.value} value={day.value}>
                {day.label}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>
            Choose the day your weekly meal plans should start. We recommend selecting the day after you typically do your grocery shopping.
          </FormHelperText>
        </FormControl>

        <FormControlLabel
          control={
            <Switch
              checked={likesLeftovers}
              onChange={(e) => {
                setLikesLeftovers(e.target.checked);
                if (!e.target.checked) setLeftoverFrequency(0);
                else if (leftoverFrequency === 0) setLeftoverFrequency(50);
              }}
            />
          }
          label={
            <Box>
              <Typography variant="body1">I like leftovers</Typography>
              <Typography variant="caption" color="text.secondary">
                We'll plan extra portions so you can enjoy easy meals later
              </Typography>
            </Box>
          }
          sx={{ mb: 2 }}
        />

        <Collapse in={likesLeftovers}>
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel id="leftover-frequency-label">When cooking dinner, how often make extra?</InputLabel>
            <Select
              labelId="leftover-frequency-label"
              id="leftover-frequency"
              value={leftoverFrequency}
              label="When cooking dinner, how often make extra?"
              onChange={(e) => setLeftoverFrequency(e.target.value as number)}
            >
              {leftoverFrequencyOptions.filter(opt => opt.value > 0).map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label} - {option.description}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              Extra portions become easy leftover meals for your next lunch or dinner
            </FormHelperText>
          </FormControl>
        </Collapse>

        <Button
          variant="contained"
          onClick={handleSavePreferences}
          disabled={prefLoading}
          startIcon={prefLoading && <CircularProgress size={20} />}
        >
          Save Preferences
        </Button>
      </Paper>
    </Container>
  );
};

export default UserPreferences;
